// src/components/ProjectBuilder/TaskList.tsx
import { useMemo } from 'react';
import { Box, Typography, Alert, Skeleton, Paper } from '@mui/material';
import { Task } from '../../services/taskApi';
import TaskCard from './TaskCard';
import { SortBy, SortOrder, GroupBy, Density } from './TaskViewOptions';

export interface TaskListProps {
  tasks: Task[];
  loading: boolean;
  error: string | null;
  onViewProject: (projectId: number) => void;
  onViewTask: (taskId: number, projectId: number) => void;
  sortBy: SortBy;
  sortOrder: SortOrder;
  groupBy: GroupBy;
  density: Density;
  userRole: 'System Admin' | 'Airport Manager' | 'Fraport User' | 'Tenant User (365 Tenant Sense)' | 'Client User';
}

export default function TaskList({
  tasks,
  loading,
  error,
  onViewProject,
  onViewTask,
  sortBy,
  sortOrder,
  groupBy,
  density,
  userRole: _userRole,
}: TaskListProps) {
  // Sort tasks
  const sortedTasks = useMemo(() => {
    const sorted = [...tasks];
    
    sorted.sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'priority':
          // Priority: Past due first (by days overdue desc), then upcoming (by days until deadline asc), then others
          if (a.isPastDue && !b.isPastDue) return -1;
          if (!a.isPastDue && b.isPastDue) return 1;
          if (a.isPastDue && b.isPastDue) {
            comparison = (b.daysOverdue || 0) - (a.daysOverdue || 0);
          } else if (a.daysUntilDeadline !== undefined && b.daysUntilDeadline !== undefined) {
            comparison = a.daysUntilDeadline - b.daysUntilDeadline;
          } else if (a.daysUntilDeadline !== undefined) return -1;
          else if (b.daysUntilDeadline !== undefined) return 1;
          else comparison = 0;
          break;
        case 'deadline': {
          const aDate = new Date(a.contractedEndDate).getTime();
          const bDate = new Date(b.contractedEndDate).getTime();
          comparison = aDate - bDate;
          break;
        }
        case 'project': {
          comparison = a.projectName.localeCompare(b.projectName);
          break;
        }
        case 'status': {
          comparison = a.status.localeCompare(b.status);
          break;
        }
        case 'airport': {
          const aAirport = a.airportName || '';
          const bAirport = b.airportName || '';
          comparison = aAirport.localeCompare(bAirport);
          break;
        }
      }
      
      return sortOrder === 'asc' ? comparison : -comparison;
    });
    
    return sorted;
  }, [tasks, sortBy, sortOrder]);

  // Group tasks
  const groupedTasks = useMemo(() => {
    if (groupBy === 'none') {
      return { 'All Tasks': sortedTasks };
    }

    const groups: Record<string, Task[]> = {};

    sortedTasks.forEach((task) => {
      let groupKey = '';
      
      switch (groupBy) {
        case 'project':
          groupKey = task.projectName;
          break;
        case 'airport':
          groupKey = task.airportName || 'No Airport';
          break;
        case 'user':
          if (task.assignedUsers.length > 0) {
            task.assignedUsers.forEach((user) => {
              const key = `${user.displayName || user.email}`;
              if (!groups[key]) groups[key] = [];
              groups[key].push(task);
            });
          } else {
            if (!groups['Unassigned']) groups['Unassigned'] = [];
            groups['Unassigned'].push(task);
          }
          return; // Early return for user grouping since we handle multiple users
        case 'userType':
          if (task.assignedUsers.length > 0) {
            const userTypes = new Set(task.assignedUsers.map(u => u.organizationType));
            userTypes.forEach((type) => {
              const key = type;
              if (!groups[key]) groups[key] = [];
              groups[key].push(task);
            });
          } else {
            if (!groups['Unassigned']) groups['Unassigned'] = [];
            groups['Unassigned'].push(task);
          }
          return; // Early return for userType grouping
        case 'status':
          groupKey = task.status;
          break;
      }

      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(task);
    });

    return groups;
  }, [sortedTasks, groupBy]);

  // Get density styles
  const getDensityStyles = () => {
    switch (density) {
      case 'compact':
        return {
          mb: 1,
          '& .MuiCard-root': { mb: 1 },
          '& .MuiCardContent-root': { py: 1.5 },
        };
      case 'list':
        return {
          mb: 0.5,
          '& .MuiCard-root': { mb: 0.5 },
          '& .MuiCardContent-root': { py: 1 },
        };
      default:
        return {
          mb: 2,
          '& .MuiCard-root': { mb: 2 },
        };
    }
  };

  if (loading) {
    return (
      <Box>
        {[...Array(3)].map((_, i) => (
          <Box key={i} sx={{ mb: 2 }}>
            <Skeleton variant="rectangular" height={150} />
          </Box>
        ))}
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (tasks.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body1" color="text.secondary">
          No tasks found matching your filters.
        </Typography>
      </Box>
    );
  }

  // Render grouped tasks
  const groupKeys = Object.keys(groupedTasks).sort();

  return (
    <Box>
      {groupKeys.map((groupKey) => {
        const groupTasks = groupedTasks[groupKey];
        const showGroupHeader = groupBy !== 'none' && groupKeys.length > 1;

        return (
          <Box key={groupKey} sx={{ mb: showGroupHeader ? 3 : 0 }}>
            {showGroupHeader && (
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  mb: 2,
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  {groupKey} ({groupTasks.length} {groupTasks.length === 1 ? 'task' : 'tasks'})
                </Typography>
              </Paper>
            )}
            {groupTasks.map((task) => (
              <Box key={task.taskId} sx={getDensityStyles()}>
                <TaskCard
                  task={task}
                  onViewProject={onViewProject}
                  onViewTask={onViewTask}
                />
              </Box>
            ))}
          </Box>
        );
      })}
    </Box>
  );
}

